/** Legacy DNS cookie scopes only; an IP Domain deletion can erase the host-only session. */
export function getLegacyAuthCookieDomains(hostname: string): string[] {
  const labels = hostname.split('.');
  if (
    labels.length < 3 ||
    hostname.includes('localhost') ||
    hostname.includes(':') ||
    /^[\d.]+$/.test(hostname)
  )
    return [];
  return [`.${labels.slice(-2).join('.')}`, hostname];
}

export type SupabaseAuthCookie = { name: string; value: string };

const AUTH_COOKIE_CHUNK_SUFFIX = /\.\d+$/;

export function isSupabaseAuthCookieName(name: string) {
  // PKCE verifier cookies share the `-auth-token` substring but are not a
  // session. Counting them as one made middleware log `invalid_session`
  // when only a leftover verifier was present.
  return name.startsWith('sb-') && name.includes('-auth-token') && !name.endsWith('-code-verifier');
}

function getAuthCookieBaseName(name: string) {
  return name.replace(AUTH_COOKIE_CHUNK_SUFFIX, '');
}

function decodeCookieValue(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function isLikelySupabaseSessionCookieValue(value: string) {
  const decoded = decodeCookieValue(value.trim());
  // @supabase/ssr writes `base64-...`; older auth helpers wrote JSON.
  // Raw token strings are the malformed parent-domain shape that crashes
  // GoTrue recovery before the response can expire the cookie.
  return (
    decoded === '' ||
    decoded.startsWith('base64-') ||
    decoded.startsWith('[') ||
    decoded.startsWith('{')
  );
}

export function getSupabaseAuthCookieNames(cookies: SupabaseAuthCookie[]) {
  return new Set(
    cookies.filter((cookie) => isSupabaseAuthCookieName(cookie.name)).map((cookie) => cookie.name),
  );
}

function groupAuthCookiesByBaseName(cookies: SupabaseAuthCookie[]) {
  const authCookiesByBaseName = new Map<string, SupabaseAuthCookie[]>();
  for (const cookie of cookies) {
    if (!isSupabaseAuthCookieName(cookie.name)) continue;
    const baseName = getAuthCookieBaseName(cookie.name);
    const group = authCookiesByBaseName.get(baseName) ?? [];
    group.push(cookie);
    authCookiesByBaseName.set(baseName, group);
  }
  return authCookiesByBaseName;
}

function chunkCookiesForBaseName(baseName: string, group: SupabaseAuthCookie[]) {
  return group
    .filter((cookie) => cookie.name.startsWith(`${baseName}.`))
    .sort(
      (a, b) =>
        Number(a.name.slice(baseName.length + 1)) - Number(b.name.slice(baseName.length + 1)),
    );
}

/**
 * `@supabase/ssr` `combineChunks` returns the exact-name cookie if present and
 * never looks at `.0` / `.1`. After the host-only + chunking migration a leftover
 * parent-domain `sb-*-auth-token` therefore hides the live host-only chunks and
 * `getUser()` becomes AuthSessionMissingError — the production login bounce.
 */
export function getStaleExactSupabaseAuthCookieNames(cookies: SupabaseAuthCookie[]) {
  const stale = new Set<string>();
  for (const [baseName, group] of groupAuthCookiesByBaseName(cookies).entries()) {
    const exactCookie = group.find((cookie) => cookie.name === baseName);
    if (exactCookie && chunkCookiesForBaseName(baseName, group).length > 0) {
      stale.add(exactCookie.name);
    }
  }
  return stale;
}

export function getMalformedSupabaseAuthCookieNames(cookies: SupabaseAuthCookie[]) {
  const malformed = new Set<string>();
  for (const [baseName, group] of groupAuthCookiesByBaseName(cookies).entries()) {
    const exactCookie = group.find((cookie) => cookie.name === baseName);
    const chunks = chunkCookiesForBaseName(baseName, group);

    if (exactCookie && chunks.length === 0) {
      if (!isLikelySupabaseSessionCookieValue(exactCookie.value)) {
        malformed.add(exactCookie.name);
      }
      continue;
    }

    if (exactCookie && !isLikelySupabaseSessionCookieValue(exactCookie.value)) {
      malformed.add(exactCookie.name);
    }

    const combinedValue = chunks.map((cookie) => cookie.value).join('');
    if (combinedValue && !isLikelySupabaseSessionCookieValue(combinedValue)) {
      chunks.forEach((cookie) => malformed.add(cookie.name));
    }
  }

  return malformed;
}

export function serializeCookieHeader(cookies: SupabaseAuthCookie[]) {
  return cookies.map((cookie) => `${cookie.name}=${cookie.value}`).join('; ');
}

function decodeCookieHeaderValue(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseCookieHeader(cookieHeader: string | null | undefined): SupabaseAuthCookie[] {
  if (!cookieHeader) return [];

  const cookies: SupabaseAuthCookie[] = [];
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const equalsIndex = trimmed.indexOf('=');
    if (equalsIndex <= 0) continue;

    const name = trimmed.slice(0, equalsIndex).trim();
    if (!name) continue;

    cookies.push({
      name,
      value: decodeCookieHeaderValue(trimmed.slice(equalsIndex + 1).trim()),
    });
  }
  return cookies;
}

export function mergeCookieCollections(
  ...collections: SupabaseAuthCookie[][]
): SupabaseAuthCookie[] {
  const merged = new Map<string, string>();
  for (const collection of collections) {
    for (const cookie of collection) {
      merged.set(cookie.name, cookie.value);
    }
  }
  return Array.from(merged, ([name, value]) => ({ name, value }));
}

export function sanitizeSupabaseAuthCookies(cookies: SupabaseAuthCookie[]) {
  const authCookieNamesToExpire = getSupabaseAuthCookieNames(cookies);
  const malformedAuthCookieNames = getMalformedSupabaseAuthCookieNames(cookies);
  const staleExactAuthCookieNames = getStaleExactSupabaseAuthCookieNames(cookies);
  const namesToStrip = new Set<string>([...malformedAuthCookieNames, ...staleExactAuthCookieNames]);
  const sanitizedCookies =
    namesToStrip.size === 0 ? cookies : cookies.filter((cookie) => !namesToStrip.has(cookie.name));

  return {
    authCookieNamesToExpire,
    malformedAuthCookieNames,
    staleExactAuthCookieNames,
    sanitizedCookies,
    sanitizedCookieHeader: serializeCookieHeader(sanitizedCookies),
  };
}

export type AuthCookieShapeSummary = {
  storeAuthCount: number;
  headerAuthCount: number;
  headerDuplicateAuthNames: number;
  exactCount: number;
  chunkCount: number;
  hasExactAndChunks: boolean;
};

/** PHI-free cookie-shape counts for login-bounce logs. Never include values. */
export function summarizeAuthCookieShape(
  storeCookies: readonly SupabaseAuthCookie[],
  cookieHeader?: string | null,
): AuthCookieShapeSummary {
  const storeAuth = storeCookies.filter((cookie) => isSupabaseAuthCookieName(cookie.name));
  const headerAuth = parseCookieHeader(cookieHeader).filter((cookie) =>
    isSupabaseAuthCookieName(cookie.name),
  );
  const headerNames = headerAuth.map((cookie) => cookie.name);
  const exactCount = storeAuth.filter(
    (cookie) => !AUTH_COOKIE_CHUNK_SUFFIX.test(cookie.name),
  ).length;
  const chunkCount = storeAuth.filter((cookie) =>
    AUTH_COOKIE_CHUNK_SUFFIX.test(cookie.name),
  ).length;

  return {
    storeAuthCount: storeAuth.length,
    headerAuthCount: headerAuth.length,
    headerDuplicateAuthNames: headerNames.length - new Set(headerNames).size,
    exactCount,
    chunkCount,
    hasExactAndChunks: getStaleExactSupabaseAuthCookieNames(storeAuth).size > 0,
  };
}

/**
 * Serialize a cookie deletion that matches production `Secure; SameSite=Lax; Path=/`
 * session cookies. Next.js `ResponseCookies` is name-keyed, so callers must
 * `headers.append('set-cookie', …)` parent-domain deletes instead of
 * `response.cookies.set` of the same name as a host-only session write.
 */
export function serializeExpiredCookie(
  name: string,
  options: { domain?: string; secure: boolean; httpOnly?: boolean },
): string {
  const parts = [
    `${name}=`,
    'Path=/',
    'Max-Age=0',
    'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
    'SameSite=Lax',
  ];
  if (options.domain) parts.push(`Domain=${options.domain}`);
  if (options.secure) parts.push('Secure');
  if (options.httpOnly) parts.push('HttpOnly');
  return parts.join('; ');
}

export function appendExpiredCookies(
  headers: { append(name: string, value: string): void },
  names: Iterable<string>,
  options: { domain?: string; secure: boolean },
) {
  for (const name of names) {
    if (!name) continue;
    // Emit both HttpOnly and non-HttpOnly deletes. Current @supabase/ssr
    // cookies are httpOnly:false; leftover parent-domain copies from older
    // adapters may be HttpOnly. Chrome matches on that flag.
    headers.append('set-cookie', serializeExpiredCookie(name, { ...options, httpOnly: false }));
    headers.append('set-cookie', serializeExpiredCookie(name, { ...options, httpOnly: true }));
  }
}
